// shared/questionsTx.js
// Firestore問題管理用サービス

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { db, auth, ADMIN_EMAIL } from '../firebase';

// ローカルキャッシュ（コスト削減用）
const CACHE_KEY = 'five_league_questions_cache';
const CACHE_TTL = 1000 * 60 * 60; // 1時間

/**
 * キャッシュから問題を取得
 */
function getCachedQuestions() {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const { questions, timestamp } = JSON.parse(cached);
    const age = Date.now() - timestamp;

    // キャッシュが1時間以内なら使用
    if (age < CACHE_TTL) {
      return questions;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * キャッシュに問題を保存
 */
function setCachedQuestions(questions) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      questions,
      timestamp: Date.now(),
    }));
  } catch (e) {
    console.warn('[questionsTx] Cache save failed:', e);
  }
}

/**
 * キャッシュをクリア（問題追加/更新時）
 */
export function clearQuestionsCache() {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
}

// ═══════════════════════════════════════════════════════════
// 問題取得（キャッシュ付き）
// ═══════════════════════════════════════════════════════════

/**
 * 承認済み問題を全件取得（キャッシュ優先）
 */
export async function getApprovedQuestions({ forceRefresh = false } = {}) {
  try {
    // キャッシュチェック
    if (!forceRefresh) {
      const cached = getCachedQuestions();
      if (cached) {
        console.log('[questionsTx] Using cached questions:', cached.length);
        return { ok: true, questions: cached, fromCache: true };
      }
    }

    // Firestoreから取得
    const q = query(collection(db, 'questions'));
    const snap = await getDocs(q);

    const questions = snap.docs.map(d => ({
      ...d.data(),
      id: d.id,
    }));

    // キャッシュに保存
    setCachedQuestions(questions);

    console.log('[questionsTx] Fetched from Firestore:', questions.length);
    return { ok: true, questions, fromCache: false };
  } catch (e) {
    console.error('[getApprovedQuestions]', e);

    // エラー時はキャッシュを返す（あれば）
    const cached = getCachedQuestions();
    if (cached) {
      return { ok: true, questions: cached, fromCache: true, error: e.message };
    }

    return { ok: false, reason: String(e?.message || e) };
  }
}

/**
 * 単一問題を取得
 */
export async function getQuestion(questionId) {
  try {
    if (!questionId) {
      return { ok: false, reason: 'bad-args' };
    }

    const ref = doc(db, 'questions', questionId);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      return { ok: false, reason: 'not-found' };
    }

    return { ok: true, question: { ...snap.data(), id: snap.id } };
  } catch (e) {
    console.error('[getQuestion]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

// ═══════════════════════════════════════════════════════════
// 管理者用：問題インポート・管理
// ═══════════════════════════════════════════════════════════

/**
 * 管理者チェック
 */
function checkAdmin() {
  if (!auth.currentUser || auth.currentUser.email !== ADMIN_EMAIL) {
    throw new Error('管理者権限がありません');
  }
}

/**
 * 問題を一括インポート（管理者用）
 * @param {Array} questions - 問題データの配列
 */
export async function importQuestions(questions) {
  try {
    checkAdmin();

    if (!questions || !Array.isArray(questions)) {
      return { ok: false, reason: 'bad-args' };
    }

    // バッチ書き込み（500件ずつ）
    const BATCH_SIZE = 500;
    let imported = 0;

    for (let i = 0; i < questions.length; i += BATCH_SIZE) {
      const batch = writeBatch(db);
      const chunk = questions.slice(i, i + BATCH_SIZE);

      for (const q of chunk) {
        const ref = doc(db, 'questions', q.id);
        batch.set(ref, {
          ...q,
          source: 'builtin',
          importedAt: serverTimestamp(),
        });
      }

      await batch.commit();
      imported += chunk.length;
      console.log(`[importQuestions] Imported ${imported}/${questions.length}`);
    }

    // キャッシュクリア
    clearQuestionsCache();

    return { ok: true, imported };
  } catch (e) {
    console.error('[importQuestions]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

/**
 * 問題を追加（管理者用）
 */
export async function addQuestion(question) {
  try {
    checkAdmin();

    if (!question || !question.id) {
      return { ok: false, reason: 'bad-args' };
    }

    const ref = doc(db, 'questions', question.id);
    await setDoc(ref, {
      ...question,
      source: question.source || 'admin',
      createdAt: serverTimestamp(),
    });

    // キャッシュクリア
    clearQuestionsCache();

    return { ok: true };
  } catch (e) {
    console.error('[addQuestion]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

/**
 * 問題を更新（管理者用）
 */
export async function updateQuestion(questionId, updates) {
  try {
    checkAdmin();

    if (!questionId || !updates) {
      return { ok: false, reason: 'bad-args' };
    }

    const ref = doc(db, 'questions', questionId);
    await updateDoc(ref, {
      ...updates,
      updatedAt: serverTimestamp(),
    });

    // キャッシュクリア
    clearQuestionsCache();

    return { ok: true };
  } catch (e) {
    console.error('[updateQuestion]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

/**
 * 問題を削除（管理者用）
 */
export async function deleteQuestion(questionId) {
  try {
    checkAdmin();

    if (!questionId) {
      return { ok: false, reason: 'bad-args' };
    }

    await deleteDoc(doc(db, 'questions', questionId));

    // キャッシュクリア
    clearQuestionsCache();

    return { ok: true };
  } catch (e) {
    console.error('[deleteQuestion]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

// ═══════════════════════════════════════════════════════════
// ユーザー投稿問題（pendingQuestions）
// ═══════════════════════════════════════════════════════════

/**
 * 問題を投稿（ユーザー用）
 */
export async function submitQuestion(question) {
  try {
    if (!auth.currentUser) {
      return { ok: false, reason: 'not-authenticated' };
    }

    if (!question || !question.answer || !question.prompt) {
      return { ok: false, reason: 'bad-args' };
    }

    const pendingId = `pending_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const ref = doc(db, 'pendingQuestions', pendingId);

    await setDoc(ref, {
      ...question,
      id: pendingId,
      status: 'pending',
      createdBy: auth.currentUser.uid,
      createdByEmail: auth.currentUser.email || null,
      createdAt: serverTimestamp(),
    });

    return { ok: true, pendingId };
  } catch (e) {
    console.error('[submitQuestion]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

/**
 * 承認待ち問題を取得（管理者用）
 */
export async function getPendingQuestions() {
  try {
    checkAdmin();

    const q = query(
      collection(db, 'pendingQuestions'),
      where('status', '==', 'pending')
    );
    const snap = await getDocs(q);

    const questions = snap.docs.map(d => ({
      ...d.data(),
      id: d.id,
    }));

    return { ok: true, questions };
  } catch (e) {
    console.error('[getPendingQuestions]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

/**
 * 投稿問題を承認（管理者用）
 */
export async function approveQuestion(pendingId, finalData = {}) {
  try {
    checkAdmin();

    if (!pendingId) {
      return { ok: false, reason: 'bad-args' };
    }

    // pending問題を取得
    const pendingRef = doc(db, 'pendingQuestions', pendingId);
    const pendingSnap = await getDoc(pendingRef);

    if (!pendingSnap.exists()) {
      return { ok: false, reason: 'not-found' };
    }

    const pending = pendingSnap.data();

    // 新しいIDを生成
    const newId = finalData.id || `user_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    // questionsコレクションに追加
    const questionRef = doc(db, 'questions', newId);
    await setDoc(questionRef, {
      ...pending,
      ...finalData,
      id: newId,
      source: 'user',
      approvedAt: serverTimestamp(),
      approvedBy: auth.currentUser.uid,
    });

    // pendingを更新
    await updateDoc(pendingRef, {
      status: 'approved',
      approvedQuestionId: newId,
      reviewedAt: serverTimestamp(),
      reviewedBy: auth.currentUser.uid,
    });

    // キャッシュクリア
    clearQuestionsCache();

    return { ok: true, questionId: newId };
  } catch (e) {
    console.error('[approveQuestion]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

/**
 * 投稿問題を却下（管理者用）
 */
export async function rejectQuestion(pendingId, reason = '') {
  try {
    checkAdmin();

    if (!pendingId) {
      return { ok: false, reason: 'bad-args' };
    }

    const pendingRef = doc(db, 'pendingQuestions', pendingId);
    await updateDoc(pendingRef, {
      status: 'rejected',
      rejectReason: reason,
      reviewedAt: serverTimestamp(),
      reviewedBy: auth.currentUser.uid,
    });

    return { ok: true };
  } catch (e) {
    console.error('[rejectQuestion]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

// ═══════════════════════════════════════════════════════════
// 問題フィードバック
// ═══════════════════════════════════════════════════════════

/**
 * 問題へのフィードバックを送信
 * @param {Object} feedback - { questionId, questionPrompt, feedbackType, message }
 */
export async function submitFeedback(feedback) {
  try {
    if (!auth.currentUser) {
      return { ok: false, reason: 'not-authenticated' };
    }

    if (!feedback || !feedback.questionId || !feedback.feedbackType) {
      return { ok: false, reason: 'bad-args' };
    }

    const feedbackId = `fb_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const ref = doc(db, 'questionFeedback', feedbackId);

    await setDoc(ref, {
      id: feedbackId,
      questionId: feedback.questionId,
      questionPrompt: feedback.questionPrompt || '',
      feedbackType: feedback.feedbackType,
      message: feedback.message || '',
      createdBy: auth.currentUser.uid,
      createdByEmail: auth.currentUser.email || null,
      createdAt: serverTimestamp(),
      status: 'new',
    });

    return { ok: true, feedbackId };
  } catch (e) {
    console.error('[submitFeedback]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

/**
 * フィードバック一覧を取得（管理者用）
 */
export async function getFeedbackList() {
  try {
    checkAdmin();

    const q = query(
      collection(db, 'questionFeedback'),
      where('status', '==', 'new')
    );
    const snap = await getDocs(q);

    const feedbacks = snap.docs.map(d => ({
      ...d.data(),
      id: d.id,
    }));

    return { ok: true, feedbacks };
  } catch (e) {
    console.error('[getFeedbackList]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

/**
 * フィードバックを処理済みにする（管理者用）
 */
export async function markFeedbackResolved(feedbackId) {
  try {
    checkAdmin();

    if (!feedbackId) {
      return { ok: false, reason: 'bad-args' };
    }

    const ref = doc(db, 'questionFeedback', feedbackId);
    await updateDoc(ref, {
      status: 'resolved',
      resolvedAt: serverTimestamp(),
      resolvedBy: auth.currentUser.uid,
    });

    return { ok: true };
  } catch (e) {
    console.error('[markFeedbackResolved]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

// ═══════════════════════════════════════════════════════════
// カテゴリ管理
// ═══════════════════════════════════════════════════════════

// デフォルトカテゴリ（ビルトイン）
const DEFAULT_CATEGORIES = [
  '動物', '食べ物', '地理', '歴史', '科学', '文化',
  'スポーツ', '音楽', '自然', '乗り物', '数学', '天気',
];

// カテゴリキャッシュ
const CATEGORY_CACHE_KEY = 'five_league_categories_cache';
const CATEGORY_CACHE_TTL = 1000 * 60 * 60; // 1時間

function getCachedCategories() {
  try {
    const cached = localStorage.getItem(CATEGORY_CACHE_KEY);
    if (!cached) return null;

    const { categories, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp < CATEGORY_CACHE_TTL) {
      return categories;
    }
    return null;
  } catch {
    return null;
  }
}

function setCachedCategories(categories) {
  try {
    localStorage.setItem(CATEGORY_CACHE_KEY, JSON.stringify({
      categories,
      timestamp: Date.now(),
    }));
  } catch {
    // ignore
  }
}

export function clearCategoriesCache() {
  try {
    localStorage.removeItem(CATEGORY_CACHE_KEY);
  } catch {
    // ignore
  }
}

/**
 * カテゴリ一覧を取得（デフォルト + 承認済みカテゴリ）
 */
export async function getCategories({ forceRefresh = false } = {}) {
  try {
    // キャッシュチェック
    if (!forceRefresh) {
      const cached = getCachedCategories();
      if (cached) {
        return { ok: true, categories: cached, fromCache: true };
      }
    }

    // Firestoreから承認済みカテゴリを取得
    const q = query(collection(db, 'categories'), orderBy('name'));
    const snap = await getDocs(q);

    const userCategories = snap.docs.map(d => d.data().name);

    // デフォルト + ユーザーカテゴリ（重複排除）
    const allCategories = [...new Set([...DEFAULT_CATEGORIES, ...userCategories])];

    setCachedCategories(allCategories);

    return { ok: true, categories: allCategories, fromCache: false };
  } catch (e) {
    console.error('[getCategories]', e);

    // エラー時はデフォルトを返す
    return { ok: true, categories: DEFAULT_CATEGORIES, fromCache: false, error: e.message };
  }
}

/**
 * カテゴリを追加（管理者用）
 */
export async function addCategory(categoryName) {
  try {
    checkAdmin();

    if (!categoryName || typeof categoryName !== 'string') {
      return { ok: false, reason: 'bad-args' };
    }

    const name = categoryName.trim();
    if (!name) {
      return { ok: false, reason: 'empty-name' };
    }

    // 既存チェック（デフォルトに含まれているかも確認）
    if (DEFAULT_CATEGORIES.includes(name)) {
      return { ok: true, message: 'already-exists-default' };
    }

    const categoryId = `cat_${name}`;
    const ref = doc(db, 'categories', categoryId);
    const existing = await getDoc(ref);

    if (existing.exists()) {
      return { ok: true, message: 'already-exists' };
    }

    await setDoc(ref, {
      id: categoryId,
      name,
      createdAt: serverTimestamp(),
      createdBy: auth.currentUser.uid,
    });

    clearCategoriesCache();

    return { ok: true, categoryId };
  } catch (e) {
    console.error('[addCategory]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

/**
 * カテゴリがデフォルトに含まれているかチェック
 */
export function isDefaultCategory(categoryName) {
  return DEFAULT_CATEGORIES.includes(categoryName);
}

// ═══════════════════════════════════════════════════════════
// Firestoreの問題が空の場合のフォールバック
// ═══════════════════════════════════════════════════════════

/**
 * 問題数を取得
 */
export async function getQuestionsCount() {
  try {
    const q = query(collection(db, 'questions'));
    const snap = await getDocs(q);
    return { ok: true, count: snap.size };
  } catch (e) {
    console.error('[getQuestionsCount]', e);
    return { ok: false, count: 0, reason: String(e?.message || e) };
  }
}
